import { isNil } from "lodash"
import type { TFunction } from "i18next"

import { ACTIVITY_SESSION_TYPE, SECTION_TYPE } from "@/app/report/common/constants"
import {
  AxisY,
  ChartRow,
  IntervalChartData,
  SessionDetailsData,
  Sort,
  SummaryImpl,
} from "@/app/report/common/constructor"
import { getDecimalPoint, getValue, parseSummary, parseUnit } from "@/app/report/common/tools"
import type { MetricMapType, SessionDetailsSettings } from "@/app/report/common/type"
import { SORT_ORDER } from "@/constants/setting"
import type { MetricMeta } from "@/types/metric"
import type {
  ChartSessionPlayerSummary,
  IntervalSessionSummaryType,
  IntervalSummaryType,
  ReportTemplate,
} from "@/types/report"
import type { Session } from "@/types/session"
import type { Unit } from "@/types/setting"
import { Summary } from "@/utils/definition"

/**
 * Session Details(세션·인터벌 차트) 섹션 — 리포트 화면(daily/page.tsx)과 PDF 빌더(BuilderSessionDetails)가
 * 함께 쓰는 단일 구현 (일감 #75). 축 구성·세션 행·인터벌 행을 만드는 규칙은 이 파일 밖에 두지 않는다.
 */

/** 템플릿 Session Details 섹션 안의 차트 하나 (settings 배열 항목) */
export type TemplateSessionDetailsChart = {
  sectionIndex: number
  chartIndex: number
  settings: SessionDetailsSettings
}

export const getTemplateSessionDetailsCharts = (
  template?: ReportTemplate | null,
): TemplateSessionDetailsChart[] =>
  (template?.content ?? []).flatMap((section, sectionIndex) =>
    section?.section === SECTION_TYPE.SESSION_DETAILS && Array.isArray(section.settings)
      ? (section.settings as SessionDetailsSettings[]).map((settings, chartIndex) => ({
          sectionIndex,
          chartIndex,
          settings,
        }))
      : [],
  )

export const resolveTemplateSessionDetailsChart = (
  charts: TemplateSessionDetailsChart[],
  sectionIndex?: number,
  chartIndex?: number,
): TemplateSessionDetailsChart | null =>
  charts.find(
    (chart) => chart.sectionIndex === sectionIndex && chart.chartIndex === (chartIndex ?? 0),
  ) ??
  charts[0] ??
  null

/** 축 구성 — 단위·소수 자릿수(평균이면 times 는 1자리)·색·차트 종류 */
export const buildSessionDetailsAxisY = ({
  settings,
  metricMap,
  selectedUnitList,
  t,
  translateKey,
}: {
  settings: SessionDetailsSettings
  metricMap: MetricMapType
  selectedUnitList: Unit[]
  t: TFunction | ((key: string) => string)
  translateKey: (key: string) => string | undefined
}): AxisY[] =>
  settings.yAxis.map((y) => {
    const metric = metricMap.get(y.metric)
    const unit =
      metric && metric.key ? (parseUnit(metric.key, selectedUnitList, metricMap) ?? null) : null

    let decimalPoint = getDecimalPoint(metric?.key, unit)
    if (settings.calcType !== "total" && unit?.toLowerCase() === "times") {
      decimalPoint = 1
    }

    return new AxisY({
      name: translateKey(y.metric) || t(`metric.${y.metric}`),
      key: y.metric,
      unit,
      decimalPoint,
      color: y.color,
      chartType: y.chartType,
      flagShow: true,
    })
  })

/**
 * 차트 설정 하나 + 데이터 → SessionDetailsData.
 * - 세션 모드: 세션별 요약(sessionPlayerSummary)으로 세션 행, 선택 세션의 인터벌 요약으로 인터벌 행
 * - 액티비티 모드: 액티비티 인터벌 요약으로 인터벌 행 하나
 */
export const buildSessionDetailsData = ({
  settings: settingsInput,
  metricMap,
  metricList,
  selectedUnitList,
  activitySessionType,
  selectedSessionIds,
  sessionSelectedRoot,
  sessionsInfo,
  sessionPlayerSummary,
  sessionIntervalSummary,
  activityIntervalSummary,
  interval,
  t,
  translateKey,
}: {
  settings: SessionDetailsSettings
  metricMap: MetricMapType
  metricList: MetricMeta[]
  selectedUnitList: Unit[]
  activitySessionType: ACTIVITY_SESSION_TYPE
  /** 인터벌 차트에 그릴 세션 (세션 모드) */
  selectedSessionIds: number[]
  /** 리포트 필터의 세션 전체 (섹션 내 세션 선택의 상한) */
  sessionSelectedRoot: number[]
  sessionsInfo: Session[]
  sessionPlayerSummary?: ChartSessionPlayerSummary[] | null
  sessionIntervalSummary?: IntervalSessionSummaryType[] | null
  /** 액티비티 인터벌 요약 — API 타입은 세션 묶음(IntervalSessionSummaryType)이나 리포트 화면은 행마다 total/average 를 읽어 왔으므로 둘 다 받는다 */
  activityIntervalSummary?: IntervalSummaryType[] | IntervalSessionSummaryType[] | null
  interval: number
  t: TFunction | ((key: string) => string)
  translateKey: (key: string) => string | undefined
}): SessionDetailsData | null => {
  if (!settingsInput || !metricList || !selectedUnitList) {
    return null
  }
  const settings = structuredClone(settingsInput)
  const metrics = settings.yAxis.map((y) => metricMap.get(y.metric))
  const { calcType } = settings
  const sort = settings.sortSetting
    ? new Sort(settings.sortSetting.key, settings.sortSetting.order.toUpperCase() as "ASC" | "DESC")
    : new Sort("name", SORT_ORDER.ASC)
  const axisY = buildSessionDetailsAxisY({ settings, metricMap, selectedUnitList, t, translateKey })

  const sessionDataTable: Array<ChartRow> = []
  const intervalChartChartDataArray: Array<IntervalChartData> = []

  if (activitySessionType === ACTIVITY_SESSION_TYPE.SESSION) {
    sessionPlayerSummary?.forEach(({ total, average, ...session }) => {
      const targetSummary = parseSummary(calcType === "total" ? total : average)
      const row = new ChartRow()
      const duration = session.dateFinish
        ? new Date(session.dateFinish).getTime() - new Date(session.dateStart).getTime()
        : 0

      row.name = session.sessionName
      row.header.set("sessionName", session.sessionName)
      row.header.set("duration", duration / 60000)
      row.header.set("playerCount", session.playerCount)
      row.header.set("idfSession", session.idfSession)
      row.header.set("time", new Date(session.dateStart).getTime())

      axisY.forEach((y, indexY) => {
        const trimmedKey = y.key.replace("Custom", "").replace("Rel", "")
        let summaryVal = (targetSummary as SummaryImpl).get(
          trimmedKey,
          metrics[indexY]?.path[0] || "general",
        )
        if (summaryVal && typeof summaryVal === "object" && "value" in summaryVal) {
          summaryVal = (summaryVal as { value: number }).value
        }
        row.data.push(!isNil(summaryVal) ? Number(summaryVal) : null)
      })
      sessionDataTable.push(row)
    })

    sessionIntervalSummary?.forEach(({ idfSession, sessionName, summaries }, indexChart) => {
      if (!selectedSessionIds.includes(idfSession)) {
        return
      }
      const dataChart = new IntervalChartData(sessionName, `S${indexChart + 1}`, [])
      summaries.forEach(({ total, average }, indexRow) => {
        const targetSummary = parseSummary(calcType === "total" ? total : average)
        const row = new ChartRow()
        row.name = `${(indexRow + 1) * interval}’`
        axisY.forEach((y, indexY) => {
          let summaryVal = (targetSummary as Summary).get(
            y.key,
            metrics[indexY]?.path[0] || "general",
          )
          if (summaryVal && typeof summaryVal === "object" && "value" in summaryVal) {
            summaryVal = (summaryVal as { value: number }).value
          }
          row.data.push(!isNil(summaryVal) ? Number(summaryVal) : null)
        })
        dataChart.data.push(row)
      })
      intervalChartChartDataArray.push(dataChart)
    })

    return new SessionDetailsData({
      settings,
      interval,
      axisY,
      sortSession: sort,
      dataSession: sessionDataTable,
      dataInterval: intervalChartChartDataArray,
      sessionList: sessionsInfo,
      sessionSelectedRoot,
      sessionSelected: selectedSessionIds,
      activitySessionType,
    })
  }

  const dataChart = new IntervalChartData("", null, [])
  activityIntervalSummary?.forEach((intervalSummary, indexRow) => {
    const row = new ChartRow()
    row.name = `${indexRow * interval}’`
    axisY.forEach((_, indexY) => {
      let value = getValue(intervalSummary, [
        settings.calcType,
        ...(metrics[indexY]?.path || "general"),
      ])
      if (value && typeof value === "object" && "value" in value) {
        value = (value as { value: number }).value
      }
      row.data.push(value)
    })
    dataChart.data.push(row)
  })
  intervalChartChartDataArray.push(dataChart)

  return new SessionDetailsData({
    settings,
    interval,
    axisY,
    sortSession: sort,
    dataSession: sessionDataTable,
    dataInterval: intervalChartChartDataArray,
    sessionList: sessionsInfo,
    activitySessionType,
  })
}
